import React from 'react';
import { useInView } from 'react-intersection-observer';

import i18n from '../../i18n';
import PropTypes from 'prop-types';
import Icon from '../../components/Icon';
import { ucFirst } from '../../utils/caseconverters';
import styles from './LocalsPresentation.module.scss';
import classNames from 'classnames';

const LocalsPresentation = ({
    image,
    title = '',
    preamble = '',
    interests = [],
    description = '',
    socialMedia = [],
    color = 'purple50',
}) => {
    const { ref, inView } = useInView({
        threshold: 0.2,
        triggerOnce: true,
    });

    return (
        <article
            className={classNames(
                styles['LocalsPresentation'],
                styles[`LocalsPresentation--${ucFirst(color)}`]
            )}>
            <div className={styles['LocalsPresentation__Wrapper']}>
                {image && image.renditions && (
                    <figure
                        ref={ref}
                        className={classNames(
                            styles['LocalsPresentation__ImageWrapper'],
                            {
                                [styles[
                                    'LocalsPresentation__ImageWrapper--IsVisible'
                                ]]: inView,
                            }
                        )}>
                        <img
                            className={styles['LocalsPresentation__Image']}
                            sizes="297px"
                            srcSet={`${image.renditions.medium.src} 600w,
                                ${image.renditions.large.src} 1200w`}
                            src={image.renditions.medium.src}
                            alt={image.alt}
                            style={{
                                objectPosition:
                                    image.focal.x + ' ' + image.focal.y,
                            }}
                        />

                        {image.caption && (
                            <figcaption
                                className={
                                    styles['LocalsPresentation__Caption']
                                }>
                                {i18n.t('generic.photo')} {image.caption}
                            </figcaption>
                        )}
                    </figure>
                )}

                <div className={styles['LocalsPresentation__Content']}>
                    <section
                        className={styles['LocalsPresentation__NameSection']}>
                        {title && (
                            <h1 className={styles['LocalsPresentation__Title']}>
                                {title}
                            </h1>
                        )}

                        {preamble && (
                            <p
                                className={
                                    styles['LocalsPresentation__Preamble']
                                }>
                                "{preamble}"
                            </p>
                        )}
                    </section>

                    {interests && interests.length > 0 && (
                        <section
                            className={
                                styles['LocalsPresentation__InterestsSection']
                            }>
                            <h2
                                className={
                                    styles[
                                        'LocalsPresentation__InterestsHeading'
                                    ]
                                }>
                                {i18n.t('LocalsPage.interests')}
                            </h2>

                            <span
                                className={
                                    styles['LocalsPresentation__TagsWrapper']
                                }>
                                {interests.map((x) => (
                                    <span
                                        className={
                                            styles['LocalsPresentation__Tag']
                                        }
                                        key={x.title}>
                                        {ucFirst(x.title)}
                                    </span>
                                ))}
                            </span>
                        </section>
                    )}

                    {description && (
                        <section
                            className={
                                styles['LocalsPresentation__AboutSection']
                            }>
                            <h2 className={styles['LocalsPresentation__About']}>
                                {i18n.t('LocalsPage.aboutMe')}
                            </h2>

                            <p
                                className={
                                    styles['LocalsPresentation__Description']
                                }>
                                {description}
                            </p>
                        </section>
                    )}

                    {socialMedia && socialMedia.length > 0 && (
                        <section
                            className={
                                styles['LocalsPresentation__SocialMediaSection']
                            }>
                            <h2
                                className={
                                    styles[
                                        'LocalsPresentation__SocialMediaTitle'
                                    ]
                                }>
                                {i18n.t('LocalsPage.followMe')}
                            </h2>

                            {socialMedia.map((x) => (
                                <a
                                    href={x.href}
                                    className={
                                        styles[
                                            'LocalsPresentation__SocialMediaItem'
                                        ]
                                    }
                                    key={x.type + x.label}>
                                    <span className="sr-only">{x.type}</span>
                                    <div
                                        className={
                                            styles[
                                                'LocalsPresentation__IconWrapper'
                                            ]
                                        }>
                                        <Icon
                                            type={x.type}
                                            color="black800"
                                            size="large"
                                        />
                                    </div>
                                    {x.label}
                                </a>
                            ))}
                        </section>
                    )}
                </div>
            </div>
        </article>
    );
};

LocalsPresentation.propTypes = {
    title: PropTypes.string.isRequired,
    preamble: PropTypes.string,
    image: PropTypes.object,
    description: PropTypes.string,
    socialMedia: PropTypes.array,
    interests: PropTypes.array,
    color: PropTypes.oneOf(['blue50', 'green50', 'purple50', 'orange50']),
};

export default LocalsPresentation;
